import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { buildTypeformUrlWithUserData } from '@/lib/utils';
import { logger } from '@/lib/logger';

export interface QualiopiForm {
  id: string;
  user_id: string;
  course_id: string;
  enrollment_id?: string;
  form_type: 'pre_training' | 'satisfaction' | 'post_training' | 'complaint';
  typeform_url: string;
  status: 'pending' | 'sent' | 'completed' | 'expired';
  sent_at?: string;
  completed_at?: string;
  expires_at?: string;
  response_data?: any;
  created_at: string;
  updated_at: string;
}

export interface PreTrainingAssessment {
  id: string;
  user_id: string;
  course_id: string;
  enrollment_id?: string;
  self_evaluation_level?: string;
  previous_experience?: string;
  learning_objectives?: string;
  availability_hours_per_week?: number;
  specific_needs?: string;
  assessment_data: any;
  analysis_notes?: string;
  adaptations_recommended?: string;
  completed_at?: string;
  analyzed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SatisfactionSurvey {
  id: string;
  user_id: string;
  course_id: string;
  enrollment_id?: string;
  survey_data: any;
  satisfaction_score?: number;
  would_recommend?: boolean;
  objectives_achieved?: boolean;
  suggestions?: string;
  completed_at: string;
  created_at: string;
  updated_at: string;
}

export interface PostTrainingSurvey {
  id: string;
  user_id: string;
  course_id: string;
  enrollment_id?: string;
  survey_data: any;
  skills_application?: string;
  career_impact?: string;
  additional_support_needed?: string;
  completed_at: string;
  created_at: string;
  updated_at: string;
}

export function useQualiopi() {
  const [forms, setForms] = useState<QualiopiForm[]>([]);
  const [assessments, setAssessments] = useState<PreTrainingAssessment[]>([]);
  const [satisfactionSurveys, setSatisfactionSurveys] = useState<SatisfactionSurvey[]>([]);
  const [postTrainingSurveys, setPostTrainingSurveys] = useState<PostTrainingSurvey[]>([]);
  const [typeformConfig, setTypeformConfig] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, profile } = useAuth();
  const { toast } = useToast();

  const fetchTypeformConfig = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('qualiopi_typeform_config')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;
      setTypeformConfig(data || []);
    } catch (error) {
      console.error('Error fetching Typeform config:', error);
    }
  }, []);

  const fetchQualiopiForms = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('qualiopi_forms')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setForms((data || []) as QualiopiForm[]);
    } catch (error) {
      console.error('Error fetching Qualiopi forms:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les formulaires Qualiopi",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  // Charger les configurations et formulaires au montage du hook
  useEffect(() => {
    fetchTypeformConfig();
    if (user?.id) {
      fetchQualiopiForms();
    }
  }, [user?.id, fetchQualiopiForms, fetchTypeformConfig]);

  // Fonction pour vérifier et créer automatiquement les formulaires à la completion d'un cours
  const checkAndCreateFormsForCourseCompletion = useCallback(async (courseId: string, enrollmentId?: string) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke('qualiopi-forms-manager', {
        body: {
          action: 'check_course_completion',
          courseId,
          userId: user.id,
          enrollmentId
        }
      });

      if (error) throw error;
      
      if (data?.completed) {
        // Recharger les formulaires pour afficher les nouveaux
        await fetchQualiopiForms();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking course completion:', error);
      return false;
    }
  }, [user?.id, fetchQualiopiForms]);

  const getQualiopiForm = async (courseId: string, formType: QualiopiForm['form_type']) => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase
        .from('qualiopi_forms')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .eq('form_type', formType)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      return (data?.[0] as QualiopiForm) || null;
    } catch (error) {
      console.error('Error fetching qualiopi form:', error);
      return null;
    }
  };

  const createQualiopiForm = async (formData: {
    user_id: string;
    course_id: string;
    enrollment_id?: string;
    form_type: 'pre_training' | 'satisfaction' | 'post_training';
    typeform_url?: string;
    expires_at?: string;
  }) => {
    try {
      // Récupérer l'URL depuis la configuration si pas fournie
      let typeformUrl = formData.typeform_url;
      if (!typeformUrl) {
        const config = typeformConfig.find(c => c.form_type === formData.form_type);
        if (!config) {
          throw new Error(`Aucune configuration Typeform trouvée pour le type: ${formData.form_type}`);
        }
        typeformUrl = config.typeform_url;
      }

      // Ajouter les paramètres utilisateur à l'URL
      const userData = {
        first_name: profile?.first_name || user?.user_metadata?.first_name,
        last_name: profile?.last_name || user?.user_metadata?.last_name,
        email: user?.email,
        user_id: user?.id
      };
      
      const finalUrl = buildTypeformUrlWithUserData(typeformUrl, userData);

      const { data, error } = await supabase
        .from('qualiopi_forms')
        .insert({
          ...formData,
          user_id: user?.id || formData.user_id,
          typeform_url: finalUrl,
          status: 'pending',
          expires_at: formData.expires_at || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      
      toast({
        title: "Formulaire créé",
        description: "Un nouveau formulaire Qualiopi a été créé"
      });
      
      return data as QualiopiForm;
    } catch (error) {
      console.error('Error creating Qualiopi form:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer le formulaire Qualiopi",
        variant: "destructive"
      });
    }
  };

  const markFormCompleted = async (formId: string, responseId?: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('qualiopi_forms')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          response_data: { response_id: responseId }
        })
        .eq('id', formId)
        .select()
        .single();

      if (error) throw error;
      
      toast({
        title: "Formulaire complété",
        description: "Merci d'avoir complété ce formulaire"
      });
      
      await fetchQualiopiForms(); // Refresh the forms list
      return data as QualiopiForm;
    } catch (error) {
      console.error('Error marking form completed:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getNextForm = useCallback(() => {
    if (!forms.length) return null;
    return forms.find(form => form.status === 'pending' && new Date(form.expires_at || '') > new Date()) || null;
  }, [forms]);
  
  const hasCompletedForm = useCallback((courseId: string, formType: QualiopiForm['form_type']) => {
    return forms.some(form => 
      form.course_id === courseId && 
      form.form_type === formType && 
      form.status === 'completed'
    );
  }, [forms]);
  
  const canShowForm = useCallback((courseId: string, formType: QualiopiForm['form_type']) => {
    return forms.some(form => 
      form.course_id === courseId && 
      form.form_type === formType && 
      form.status === 'pending' &&
      new Date(form.expires_at || '') > new Date()
    );
  }, [forms]);
  
  const shouldShowPreAssessment = useCallback((courseId: string) => {
    return canShowForm(courseId, 'pre_training');
  }, [canShowForm]);
  
  const shouldShowSatisfactionSurvey = useCallback((courseId: string) => {
    return canShowForm(courseId, 'satisfaction');
  }, [canShowForm]);
  
  const shouldShowPostSurvey = useCallback((courseId: string) => {
    return canShowForm(courseId, 'post_training');
  }, [canShowForm]);
  
  // Additional mock functions for compatibility
  const getOrganismeInfo = () => ({ 
    name: 'NoCodeo', 
    nom: 'NoCodeo',
    nda: '11931186393', 
    address: 'Paris, France',
    siret: '93369512400018',
    referent_pedagogique: 'Équipe NoCodeo',
    delai_acces: '72 heures',
    accessibilite_handicap: 'Accessibilité handicap : nous consulter',
    modalites_evaluation: 'Quiz intermédiaires, évaluation finale',
    qualiopi: true
  });
  const hasNDA = true;
  const isQualiopiCertified = true;
  const adminConfig = null;
  const refetchConfig = fetchTypeformConfig;
  
  const getComplaintFormUrl = useCallback(() => {
    if (!user || !user.email) return null;
    
    // Récupérer l'URL du formulaire de réclamation depuis la configuration
    const complaintConfig = typeformConfig.find(config => config.form_type === 'complaint');
    if (!complaintConfig) {
      logger.warn('Aucune configuration trouvée pour le formulaire de réclamation');
      return null;
    }
    
    const userData = {
      first_name: profile?.first_name || user.user_metadata?.first_name,
      last_name: profile?.last_name || user.user_metadata?.last_name,
      email: user.email,
      user_id: user.id
    };
    
    return buildTypeformUrlWithUserData(complaintConfig.typeform_url, userData);
  }, [user, profile, typeformConfig]);

  return {
    forms,
    assessments,
    satisfactionSurveys,
    postTrainingSurveys,
    loading,
    fetchQualiopiForms,
    getQualiopiForm,
    createQualiopiForm,
    markFormCompleted,
    getNextForm,
    hasCompletedForm,
    canShowForm,
    shouldShowPreAssessment,
    shouldShowSatisfactionSurvey,
    shouldShowPostSurvey,
    checkAndCreateFormsForCourseCompletion,
    getOrganismeInfo,
    hasNDA,
    isQualiopiCertified,
    adminConfig,
    typeformConfig,
    refetchConfig,
    getComplaintFormUrl
  };
}